/**
 * JournalEntriesFilterBar — FilterBar wired to journal entries state.
 */

import { FilterBar } from '@elsapiens/ui'

import { buildStatusOptions, buildTypeOptions } from './journal-entries-helpers'

import type { FilterBarCondition } from '@elsapiens/ui'


// ── Tab definitions (stable, defined once) ────────────────────────────────────

export const JOURNAL_TABS = [
  { id: 'all', label: 'All', conditions: [] as FilterBarCondition[] },
  {
    id: 'drafts',
    label: 'Drafts',
    conditions: [
      {
        id: 'tab-status',
        fieldId: 'status',
        operator: 'equals' as const,
        value: 'ENTRY_STATUS_DRAFT',
      },
    ] as FilterBarCondition[],
  },
  {
    id: 'posted',
    label: 'Posted',
    conditions: [
      {
        id: 'tab-status',
        fieldId: 'status',
        operator: 'equals' as const,
        value: 'ENTRY_STATUS_POSTED',
      },
    ] as FilterBarCondition[],
  },
  {
    id: 'voided',
    label: 'Voided',
    conditions: [
      {
        id: 'tab-status',
        fieldId: 'status',
        operator: 'equals' as const,
        value: 'ENTRY_STATUS_VOIDED',
      },
    ] as FilterBarCondition[],
  },
]

export interface JournalEntriesFilterBarProps {
  activeTab: string
  conditions: FilterBarCondition[]
  searchQuery: string
  sortField: string
  sortDirection: 'asc' | 'desc'
  onSearchChange: (q: string) => void
  onConditionsChange: (c: FilterBarCondition[]) => void
  onActiveTabChange: (id: string) => void
  onSortChange: (field: string, dir: 'asc' | 'desc') => void
  onRefresh: () => void
}

const SORT_OPTIONS = [
  { id: 'entryDate', label: 'Entry Date' },
  { id: 'entryNumber', label: 'Entry Number' },
  { id: 'totalDebit', label: 'Amount' },
  { id: 'status', label: 'Status' },
]

function buildFilterFields(): React.ComponentProps<typeof FilterBar>['fields'] {
  return [
    { id: 'status', label: 'Status', type: 'select', options: buildStatusOptions() },
    { id: 'entryType', label: 'Entry Type', type: 'select', options: buildTypeOptions() },
    { id: 'entryDate', label: 'Entry Date', type: 'date' },
  ]
}

export function JournalEntriesFilterBar({
  activeTab,
  conditions,
  searchQuery,
  sortField,
  sortDirection,
  onSearchChange,
  onConditionsChange,
  onActiveTabChange,
  onSortChange,
  onRefresh,
}: JournalEntriesFilterBarProps): JSX.Element {
  function handleTabChange(tabId: string): void {
    onActiveTabChange(tabId)
    const tab = JOURNAL_TABS.find(t => t.id === tabId)
    if (tab) { onConditionsChange(tab.conditions) }
  }

  function handleConditionsChange(c: FilterBarCondition[]): void {
    onConditionsChange(c)
    onActiveTabChange('')
  }

  return (
    <FilterBar
      firstInCard
      inCard
      activeTab={activeTab}
      conditions={conditions}
      fields={buildFilterFields()}
      search={{ value: searchQuery, onChange: onSearchChange, placeholder: 'Search entries...' }}
      sort={{
        options: SORT_OPTIONS,
        value: sortField,
        direction: sortDirection,
        onChange: (id, dir) => { onSortChange(id, dir) },
      }}
      tabs={JOURNAL_TABS}
      onConditionsChange={handleConditionsChange}
      onRefresh={onRefresh}
      onTabChange={handleTabChange}
    />
  )
}
